import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * Shared cross-script cart-mutation queue. Other scripts on the storefront (e.g. the
 * free-gift automation) route their own /cart/*.js writes through this same
 * window-scoped queue, so that two independently triggered cart mutations are never
 * in flight at the same time. Shopify's AJAX Cart API is not transactional across
 * concurrent requests - two unrelated writes racing against each other is a real,
 * documented source of duplicate or lost cart lines. Serializing every write here
 * removes that risk regardless of which script initiated it.
 * @param {() => Promise<any>} fn
 * @returns {Promise<any>}
 */
function withCartLock(fn) {
  const next = (window.__cartMutationQueue || Promise.resolve()).then(fn, fn);
  window.__cartMutationQueue = next.catch(() => {});
  return next;
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Finds the row for a given cart line, preferring its stable line item key.
   * Falling back to the (possibly stale) 1-based line index only when no key is available.
   * A key-first lookup is what keeps quantity updates pointed at the correct row even when
   * other lines are inserted/removed elsewhere in the cart (e.g. an automatic free-gift line
   * from a Buy-X-Get-Y discount), which otherwise shifts every line index below it.
   * @param {number | undefined} line - The 1-based line index (fallback identifier).
   * @param {string | undefined} key - The stable line item key (preferred identifier).
   * @returns {HTMLTableRowElement | undefined}
   */
  #findRow(line, key) {
    if (key) {
      const row = this.refs.cartItemRows.find((row) => row.dataset.key === key);
      if (row) return row;
    }
    return line ? this.refs.cartItemRows[line - 1] : undefined;
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line, cartKey: key } = event.detail;

    // Cart items require either a stable line key or (fallback) a line number
    if (!line && !key) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line, key);
    }

    this.updateQuantity({
      line,
      key,
      quantity,
      action: 'change',
    });

    const lineItemRow = this.#findRow(line, key);

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Framework on:click handler for the remove ("trash") button — bound via
   * on:click="/onRemoveButtonClick/{{ item.key }}" in cart-products.liquid.
   * The component framework always invokes on:click handlers as (data, event), where
   * `data` is the single value parsed from the on:click attribute (here, the cart
   * line's stable item key) — it does not support multiple positional arguments.
   * This wrapper exists so onLineItemRemove (used both here and internally by
   * #onQuantityChange with explicit line/key arguments) never accidentally receives
   * the raw click Event in place of a key.
   * @param {string} key - The stable line item key, from the on:click attribute.
   */
  onRemoveButtonClick(key) {
    this.onLineItemRemove(undefined, key);
  }

  /**
   * Handles the line item removal.
   * @param {number} [line] - The line item index (fallback identifier).
   * @param {string} [key] - The stable line item key (preferred identifier).
   */
  onLineItemRemove(line, key) {
    this.updateQuantity({
      line,
      key,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.#findRow(line, key);

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} [config.line] - The line (fallback identifier, used only when no key is available).
   * @param {string} [config.key] - The stable line item key (preferred identifier for /cart/change.js).
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, key, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    // Prefer the stable line item key (Shopify's `id` param on /cart/change.js) over the
    // positional `line` index. The index shifts whenever another line is inserted or removed
    // anywhere in the cart (for example an automatic free-gift line from a Buy-X-Get-Y discount),
    // which can otherwise cause this request to target the wrong row and create a duplicate line
    // instead of updating the intended one.
    const identifier = key ? { id: key } : { line: line };

    const body = JSON.stringify({
      ...identifier,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    withCartLock(() => fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body })))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, key, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number | undefined} line - The line (best-effort, used only for displaying the error in the right row).
   * @param {string | undefined} key - The stable line item key, preferred for locating the row.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, key, parsedResponseText) => {
    const row = this.#findRow(line, key);
    const resolvedLine = row ? this.refs.cartItemRows.indexOf(row) + 1 : line;

    const quantitySelector = resolvedLine ? this.refs.quantitySelectors[resolvedLine - 1] : undefined;
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${resolvedLine}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${resolvedLine}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
